import { Component, Inject, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ContactsService } from 'src/app/applicationModules/company/services/contacts.service';
import { CoreService } from 'src/app/applicationModules/core/services/coreService/core.service';
import { StatusConstant } from 'src/app/middleware/common/status/status-constant';

import {
  ContactListApiModel,
  CoreCondition,
  CoreConditionApiModel,
  CoreConditionsRequest,
  CoreEducationalMaterialRequest,
  EducationalMaterialApiModel,
  EducationalMaterialItem,
  EducationalMaterialPerson,
  PersonalMessagePromptTemplate,
  PreparePersonalMessageRequest,
  PreparePersonalMessageResponse,
  SendChannel,
  SendEducationalMaterialRequest,
  WelcomeEmailContact,
  WelcomeEmailDialogData,
  WelcomeEmailResult,
} from './welcome-email.model';
import { ImportMyDriveListComponent } from '../importMyDrive/importMyDrive.component';
import { ModuleName } from '../Enum/common.enum';
import { FilteredContactVm } from 'src/app/viewModels/core/contacts-vm';
import { LoaderService } from '../../shared/loader/loader.service';
import { SettingService } from 'src/app/applicationModules/settings/services/settingService/setting.service';

/**
 * Send Template ("Welcome Email") dialog.
 *
 * Opened from the Email tab on the Core Detail page. It manages a small,
 * well-defined slice of state:
 *
 *   1. `contacts` / `chosen` — the contact picker and what is ticked
 *   2. `channel`             — email or text
 *   3. `personalNote` / `attachments` / `scheduledAt` — the send options
 *   4. `sending`             — the in-flight send guard
 *
 * Contacts come from `Contact/GetCoreSelectedContact` via the existing
 * `ContactsService` — the contacts attached to this core, both the record's
 * own and those shared with it. No new endpoint and no new service. The
 * caller may also hand a ready-made list in through MAT_DIALOG_DATA, in
 * which case the dialog skips the request.
 *
 * Send posts to `AIAutomation/SendCoreEducationalMaterialToContacts` with the
 * selected people, then closes with a `WelcomeEmailResult` so the caller can
 * refresh its own list.
 */
@Component({
  selector: 'app-welcome-email',
  templateUrl: './welcome-email.component.html',
  styleUrls: ['./welcome-email.component.scss'],
})
export class WelcomeEmailComponent implements OnInit, OnDestroy {

  /** Everything the endpoint returned for this core. */
 // allContacts: WelcomeEmailContact[] = [];
  allContacts: any=[] = [];
  
  /** The contacts currently listed (i.e. matching the search term). */
  contacts: WelcomeEmailContact[] = [];

  /**
   * The ticked contacts, held as whole records rather than ids. Searching
   * replaces `contacts`, so storing only ids would silently drop recipients
   * that fell out of the current result set.
   */
  chosen: WelcomeEmailContact[] = [];

  /**
   * Search term. This endpoint takes no search parameter -- it returns the
   * core's whole contact circle -- so the filter is applied locally.
   */
  searchTerm = '';

  /** True while the contact list request is in flight. */
  loading = false;

  /** Set when the contact-list call fails, so the user can retry. */
  loadError = '';

  /** Channel picked in the Send as toggle. */
  channel: SendChannel = 'email';

  /** Heading for the sender's own message. */
  personalNoteTitle = '';

  /** Free text the user can add alongside the material. */
  personalNote = '';

  /** Files picked through the Attach action. */
  attachments: File[] = [];

  /** `datetime-local` value; empty means send immediately. */
  scheduledAt = '';

  /** Whether the inline schedule field is open. */
  showSchedule = false;

  /** Whether the "add another recipient" form is open. */
  showAddRecipient = false;

  /** The new-contact form. Name and email are required, phone is not. */
  newContact = { name: '', email: '', phone: '' };

  /** Validation message shown under the new-contact form. */
  manualError = '';

  /** True while AddContact is in flight. */
  savingContact = false;

  /**
   * Contact field definitions for the Contact folder. AddContact takes a
   * `Fields` array keyed by the server's own field ids, so we look them up
   * rather than guessing.
   */
  private contactFields: any[] = [];

  /** True while a send is in flight, so the button cannot be double-fired. */
  sending = false;

  private readonly destroy$ = new Subject<void>();

  /** True once the first load has finished, so reloads stop re-ticking. */
  private initialised = false;

  private readonly emailPattern: RegExp =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  /**
   * Candidate keys for a mobile number on the contact payload. The endpoint is
   * shared across screens and does not document a phone column, so we accept
   * whichever of these it populates instead of hard-coding a guess. Add the
   * real key here once it is confirmed and the rest keeps working.
   */
  private readonly mobileKeys: string[] = [
    'mobile', 'mobileNumber', 'mobilePhone', 'cellPhone', 'cellNumber',
    'phone', 'phoneNumber', 'contactNumber', 'primaryPhone',
  ];

  /** Conditions recorded against this core, shown as chips beside the toggle. */
  conditions: CoreCondition[] = [];

  /** True while the conditions request is in flight. */
  conditionsLoading = false;

  /** The add-condition form, held open by `addConditionDialogRef`. */
  newCondition = { display: '', code: '' };

  /** Validation message shown inside the add-condition popup. */
  conditionError = '';

  private addConditionDialogRef: MatDialogRef<any>;

  /** Whether the educational-content side panel is open. */
  showEducationalPanel = false;

  /** The library rows for this core, each with its own tick state. */
  educationalMaterials: EducationalMaterialItem[] = [];

  /** True while the educational-material request is in flight. */
  materialsLoading = false;

  /** Set when the educational-material call fails, so the user can retry. */
  materialsError = '';

  /**
   * The condition row the current material list was fetched for. Reopening the
   * panel refetches only when the conditions have changed since, so adding or
   * removing a chip is reflected without a request on every toggle.
   */
  private materialsLoadedFor: string = null;

  /** Dialog width with the side panel closed and open. */
  // Viewport-relative, so the dialog keeps its proportions on any screen; the
  // floor and the 96vw cap both live in `.welcome-email-dialog` in styles.scss.
  private readonly baseDialogWidth = '65vw';
  private readonly wideDialogWidth = '88vw';

  ContactName: string = "";
  mainModuleName: string = "";
  //coreID:number=0;
  coreIncludeinEmil: boolean = true;
  coreDetails: any = [] = [];
  coreName: string = "";

  constructor(
    private dialogRef: MatDialogRef<WelcomeEmailComponent>,
    private toastr: ToastrService,
    private coreService: CoreService,
    private statusConst: StatusConstant, private dialog: MatDialog,
    private filteredContactVm: FilteredContactVm, private loaderService: LoaderService,
    private settingService: SettingService,
    @Inject(MAT_DIALOG_DATA) public data: WelcomeEmailDialogData
  ) { }

  ngOnInit(): void {

    var menuModulesList = JSON.parse(sessionStorage.getItem('menuModules') || '[]');
    var coreName = [];
    coreName = menuModulesList.filter((x: any) => x.mainModuleName == ModuleName.Core);
    if (coreName.length > 0) {
      this.mainModuleName = coreName[0].mainModuleName;//"Core";
      this.coreName = coreName[0].orgModuleName;
      //this.CorefolderID = coreName[0].folderID;
    }

    var contName = [];
    contName = menuModulesList.filter((x: any) => x.mainModuleName == ModuleName.Contact);
    if (contName.length > 0) {
      this.ContactName = contName[0].orgModuleName;
    }

    this.coreID = Number(this.data && this.data.coreID) || 0;

    this.coreDetails = this.data.coreData;


    this.loadConditions();

    const supplied = this.data && this.data.contacts ? this.data.contacts : [];
    if (supplied.length) {
      // Caller handed us a list -- use it and skip the request.
      this.allContacts = supplied.slice();
      this.applyFilter();
      this.syncSelection();
      return;
    }

    this.loadContacts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ======================================================================
  // Contact list (Contact/GetCoreSelectedContact)
  // ======================================================================

  /** Filter locally on every keystroke; the list is already in memory. */
  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.applyFilter();
  }

  /** Narrow the visible rows to those matching the search term. */
  private applyFilter(): void {
    const needle = (this.searchTerm || '').trim().toLowerCase();
    if (!needle) {
      this.contacts = this.allContacts.slice();
      return;
    }
    this.contacts = this.allContacts.filter((contact) =>
      [contact.name, contact.relationship, contact.email, contact.mobile]
        .some((field) => (field || '').toLowerCase().indexOf(needle) !== -1)
    );
  }

  // Summary
  // Load the contacts attached to this core -- the record's own contacts plus
  // any shared with it. Mirrors the request model the Documents screen already
  // sends to this endpoint.
  loadContacts(): void {
    this.loading = true;
    this.loadError = '';

    const reqModel = {
      databaseID: Number(this.data && this.data.databaseID) || 0,
      organizationID: Number(this.data && this.data.organizationID) || 0,
      userID: this.data ? this.data.userID : null,
      coreID: Number(this.data && this.data.coreID) || 0,
    };

    this.coreService.getNormalAndSharedContacts(reqModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.loading = false;

          if (!response || response.status !== this.statusConst.statusSuccess) {
            this.allContacts = [];
            this.contacts = [];
            this.loadError =
              (response && response.message) || 'Could not load the contacts for this record.';
            return;
          }

          const normal: ContactListApiModel[] =
            (response.data && response.data.contactListModel) || [];
          const shared: ContactListApiModel[] =
            (response.data && response.data.sharedContactListModel) || [];

          const mapped = normal
            .map((item) => this.mapContact(item, false))
            .concat(shared.map((item) => this.mapContact(item, true)));

          // A contact can appear in both lists; keep the first (unshared) copy.
          const seen: { [id: string]: boolean } = {};
          this.allContacts = mapped.filter((contact) => {
            if (seen[contact.id]) {
              return false;
            }
            seen[contact.id] = true;
            return true;
          });

          if (this.coreDetails.length > 0) {
            var email: any = [];
            var mobile: any = [];
            var name: any = [];
            name = this.coreDetails.filter((x: any) => x.fieldName == 'Name');
            email = this.coreDetails.filter((x: any) => x.fieldName == 'Email');
            mobile = this.coreDetails.filter((x: any) => x.fieldName == 'Phone');

            const cores1 = {
              badge: "",
              email: email[0].fieldValue,
              id: String(this.coreID),
              mobile: mobile[0].fieldValue,
              name: name[0].fieldValue,
              prefers: "email",
              relationship: this.coreName
            };

            this.allContacts.unshift(cores1);
          }

          this.applyFilter();
          this.syncSelection();
        },
        () => {
          this.loading = false;
          this.allContacts = [];
          this.contacts = [];
          this.loadError = 'Could not load the contacts for this record.';
        }
      );
  }

  // ======================================================================
  // Conditions (AIAutomation/GetConditionsForCore)
  // ======================================================================

  // Summary
  // Load the conditions recorded against this core. They are decoration on the
  // send dialog, not a precondition for sending, so a failure leaves the chip
  // row empty and is not surfaced as an error.
  loadConditions(): void {
    const coreID = Number(this.data && this.data.coreID) || 0;
    if (!coreID) {
      this.conditions = [];
      return;
    }

    const reqModel: CoreConditionsRequest = {
      coreID,
      userID: this.data && this.data.userID ? String(this.data.userID) : '',
      organizationID: Number(this.data && this.data.organizationID) || 0,
      officeID: Number(this.data && this.data.officeID) || 0,
      databaseID: Number(this.data && this.data.databaseID) || 0,
    };

    this.conditionsLoading = true;

    this.coreService.getConditionsForCore(reqModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.conditionsLoading = false;

          if (!response || response.status !== this.statusConst.statusSuccess) {
            this.conditions = [];
            return;
          }

          const rows: CoreConditionApiModel[] =
            Array.isArray(response.data) ? response.data : [];

          this.conditions = rows
            .map((row, index) => this.mapCondition(row, index))
            .filter((condition) => !!condition.name);
        },
        () => {
          this.conditionsLoading = false;
          this.conditions = [];
        }
      );
  }

  /** Translate one condition row into the chip the header renders. */
  private mapCondition(row: CoreConditionApiModel, index: number): CoreCondition {
    const name = row && row.display ? String(row.display).trim() : '';
    const code = row && row.code ? String(row.code).trim() : '';

    return {
      // The payload has no id and can repeat a code, so the index keeps
      // trackBy identities unique.
      id: (code || 'condition') + '-' + index,
      name,
      code,
      initials: this.conditionInitials(name),
    };
  }

  // Summary
  // Up to two letters for the circle: the first letter of the first two words,
  // or the first two letters when the condition is a single word.
  conditionInitials(name: string): string {
    const parts = (name || '').split(/[\s\-/,]+/).filter((part) => !!part);
    if (!parts.length) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].substr(0, 2).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  /** Stable identity for *ngFor over the condition chips. */
  trackByCondition(index: number, condition: CoreCondition): string {
    return condition.id;
  }

  // Summary
  // Drop one condition from the chip row. Local to this dialog, the mirror of
  // `addCondition` -- nothing is posted, so the row reverts to what
  // GetConditionsForCore returns the next time the dialog opens.
  removeCondition(condition: CoreCondition): void {
    this.conditions = this.conditions.filter((item) => item.id !== condition.id);
  }

  /** Open the add-condition popup on an empty form. */
  openAddCondition(templateRef: TemplateRef<any>): void {
    this.newCondition = { display: '', code: '' };
    this.conditionError = '';
    this.addConditionDialogRef = this.dialog.open(templateRef, {
      panelClass: 'custom-modalbox',
      width: '420px',
    });
  }

  closeAddCondition(): void {
    if (this.addConditionDialogRef) {
      this.addConditionDialogRef.close();
    }
  }

  // Summary
  // Append the typed condition to the chip row. This is a local addition only:
  // nothing is posted back, so the list reverts to what GetConditionsForCore
  // returns the next time the dialog opens.
  addCondition(): void {
    const display = (this.newCondition.display || '').trim();
    const code = (this.newCondition.code || '').trim();

    if (!display) {
      this.conditionError = 'Enter the condition name.';
      return;
    }

    const duplicate = this.conditions.some((condition) =>
      condition.name.toLowerCase() === display.toLowerCase());
    if (duplicate) {
      this.conditionError = 'That condition is already listed.';
      return;
    }

    this.conditions = this.conditions.concat({
      id: (code || 'condition') + '-added-' + this.conditions.length,
      name: display,
      code,
      initials: this.conditionInitials(display),
    });

    this.closeAddCondition();
  }

  // ======================================================================
  // Educational content (AIAutomation/GetCoreEducationalMaterial)
  // ======================================================================

  // Summary
  // Open or close the side panel. The dialog is opened at a fixed width by the
  // caller, so it is widened here rather than there -- the panel is this
  // dialog's own state and the caller should not have to know about it.
  toggleEducationalPanel(): void {
    this.showEducationalPanel = !this.showEducationalPanel;

    this.dialogRef.updateSize(
      this.showEducationalPanel ? this.wideDialogWidth : this.baseDialogWidth);

    if (this.showEducationalPanel && this.materialsLoadedFor !== this.conditionsSignature()) {
      this.loadEducationalMaterial();
    }
  }

  // Summary
  // The chip row reduced to a comparable string. Used only to decide whether
  // the material list is still the one these conditions asked for.
  private conditionsSignature(): string {
    return this.conditions.map((condition) => condition.code + '|' + condition.name).join('~');
  }

  // Summary
  // Load the educational content available for this core. Every row arrives
  // ticked, so the panel opens on "send everything" and the user unticks what
  // they do not want.
  loadEducationalMaterial(): void {
    const coreID = Number(this.data && this.data.coreID) || 0;
    if (!coreID) {
      this.educationalMaterials = [];
      return;
    }

    const signature = this.conditionsSignature();

    const reqModel: CoreEducationalMaterialRequest = {
      coreID,
      // The chip row as it stands, additions and removals included.
      conditions: this.conditions.map((condition) => ({
        display: condition.name,
        code: condition.code,
      })),
      userID: this.data && this.data.userID ? String(this.data.userID) : '',
      organizationID: Number(this.data && this.data.organizationID) || 0,
      officeID: Number(this.data && this.data.officeID) || 0,
      databaseID: Number(this.data && this.data.databaseID) || 0,
    };

    this.materialsLoading = true;
    this.materialsError = '';

    this.coreService.getCoreEducationalMaterial(reqModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.materialsLoading = false;

          if (!response || response.status !== this.statusConst.statusSuccess) {
            this.educationalMaterials = [];
            this.materialsError =
              (response && response.message) || 'Could not load the educational content.';
            return;
          }

          const rows: EducationalMaterialApiModel[] =
            Array.isArray(response.data) ? response.data : [];

          // A refetch replaces the library rows only -- My Drive picks are the
          // user's own additions and survive a change to the conditions.
          const picked = this.educationalMaterials.filter((material) => material.source === 'drive');

          this.educationalMaterials = rows.map((row) => this.mapMaterial(row)).concat(picked);
          this.materialsLoadedFor = signature;

          // With the content in hand, draft a note to go with it. No prompt is
          // sent: the conditions alone are what the draft is built from.
          this.postPrepare(this.buildPrepareRequest('', 250));
        },
        () => {
          this.materialsLoading = false;
          this.educationalMaterials = [];
          this.materialsError = 'Could not load the educational content.';
        }
      );
  }

  /** Re-run the educational-material load after a failure. */
  retryMaterials(): void {
    this.loadEducationalMaterial();
  }

  /** Translate one library row into the panel row, ticked by default. */
  private mapMaterial(row: EducationalMaterialApiModel): EducationalMaterialItem {
    const id = Number(row && row.id) || 0;

    return {
      key: 'library-' + id,
      source: 'library',
      id,
      title: row && row.title ? String(row.title).trim() : 'Untitled',
      description: row && row.description ? String(row.description).trim() : '',
      contentType: Number(row && row.contentType) || 0,
      fileType: row && row.fileType ? String(row.fileType).trim() : '',
      url: row && row.url ? String(row.url) : '',
      time: Number(row && row.time) || 0,
      selected: true,
    };
  }

  toggleMaterial(material: EducationalMaterialItem): void {
    material.selected = !material.selected;
  }

  /** Keys of the materials whose full description is showing. */
  private expandedMaterials = new Set<string>();

  isMaterialExpanded(material: EducationalMaterialItem): boolean {
    return this.expandedMaterials.has(material.key);
  }

  // Clicking the title or description shows / hides the full description
  // without changing the row's selection.
  toggleMaterialExpand(material: EducationalMaterialItem, event: Event): void {
    if (!material.description) {
      return;
    }
    event.stopPropagation();
    if (this.expandedMaterials.has(material.key)) {
      this.expandedMaterials.delete(material.key);
    } else {
      this.expandedMaterials.add(material.key);
    }
  }

  selectAllMaterials(): void {
    this.educationalMaterials.forEach((material) => { material.selected = true; });
  }

  clearMaterialSelection(): void {
    this.educationalMaterials.forEach((material) => { material.selected = false; });
  }

  /** The ticked rows, in the order the library returned them. */
  get selectedMaterials(): EducationalMaterialItem[] {
    return this.educationalMaterials.filter((material) => material.selected);
  }

  get selectedMaterialCount(): number {
    return this.selectedMaterials.length;
  }

  // Summary
  // The ticked educational content, in the shape the send endpoint takes --
  // the row as GetCoreEducationalMaterial returned it, without the panel's own
  // `key`, `source` and `selected` bookkeeping. Unticked rows are left out.
  private buildFiles(): EducationalMaterialApiModel[] {
    return this.selectedMaterials.map((material) => ({
      id: material.id,
      title: material.title,
      description: material.description,
      contentType: material.contentType,
      fileType: material.fileType,
      url: material.url,
      time: material.time,
    }));
  }

  /** Stable identity for *ngFor over the material rows. */
  trackByMaterial(index: number, material: EducationalMaterialItem): string {
    return material.key;
  }

  // Summary
  // Pick a file from My Drive and add it to the content list. Uses the shared
  // ImportMyDriveListComponent the core listing and eSignature screens already
  // open, so the browsing, sharing and upload behaviour stays in one place.
  openMyDrive(): void {
    const dialogRef = this.dialog.open(ImportMyDriveListComponent, {
      data: {
        moduleName: this.mainModuleName,
        panelClass: 'task_template',
      },
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (!result || !result.url) {
          return; // cancelled, or closed without a pick
        }
        this.addDriveMaterial(result);
      });
  }

  // Summary
  // Append a My Drive pick to the panel, ticked like everything else. The
  // component closes with { attachType, url, type, filePathurl, fileManagerID },
  // where `url` is the file name and `filePathurl` the path.
  private addDriveMaterial(result: any): void {
    const fileManagerID = Number(result.fileManagerID) || 0;
    const key = 'drive-' + (fileManagerID || this.educationalMaterials.length);

    if (this.educationalMaterials.some((material) => material.key === key)) {
      return; // already in the list
    }

    const title = String(result.url).trim();

    this.educationalMaterials = this.educationalMaterials.concat({
      key,
      source: 'drive',
      id: fileManagerID,
      title,
      description: '',
      contentType: 0,
      fileType: this.readFileType(title),
      url: result.filePathurl ? String(result.filePathurl) : '',
      time: 0,
      selected: true,
    });
  }

  /** Extension of a file name, upper-cased, or empty when it has none. */
  private readFileType(fileName: string): string {
    const dot = (fileName || '').lastIndexOf('.');
    if (dot < 0 || dot === fileName.length - 1) {
      return '';
    }
    return fileName.substr(dot + 1).toUpperCase();
  }

  /** Re-run the load after a failure. */
  retryLoad(): void {
    this.loadContacts();
  }

  // Summary
  // Translate one API row into the shape the picker renders. Kept in one place
  // so a change to the payload only touches this method. The role recorded
  // against the core becomes the relationship line under the name.
  private mapContact(item: ContactListApiModel, isShared: boolean): WelcomeEmailContact {
    const roles = item.contactCoreRoleMappingModels || [];
    const roleNames = roles
      .map((role) => (role && role.rloeName ? String(role.rloeName).trim() : ''))
      .filter((name) => !!name);

    return {
      id: String(item.contactID),
      name: item.name || '',
      relationship: roleNames.length ? roleNames.join(' \u00b7 ') : 'Contact',
      badge: isShared ? 'Shared' : '',
      email: item.email || '',
      mobile: this.readMobile(item),
      // The endpoint carries no channel preference, so everyone defaults to
      // email. Map the real column here once the API exposes one.
      prefers: 'email',
    };
  }

  /** First populated phone-ish field on the payload, or empty. */
  private readMobile(item: ContactListApiModel): string {
    for (let i = 0; i < this.mobileKeys.length; i++) {
      const value = item[this.mobileKeys[i]];
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  }

  // Summary
  // Reconcile the ticked list with a freshly loaded one. On the first load
  // the dialog auto-populates -- the point of the popup is that recipients
  // arrive ready -- but a reload after adding a contact must not re-tick
  // people the user deliberately unticked, so later loads only re-point the
  // existing selection at the new record objects.
  private syncSelection(): void {
    if (!this.initialised) {
      this.initialised = true;

      const ids = this.data && this.data.preselectedIds ? this.data.preselectedIds : null;
      const candidates = ids
        ? this.allContacts.filter((c) => ids.indexOf(c.id) !== -1)
        : this.allContacts;

      this.chosen = candidates.filter((contact) => this.isReachable(contact));
      return;
    }

    const chosenIds = this.chosen.map((contact) => contact.id);
    this.chosen = this.allContacts.filter(
      (contact) => chosenIds.indexOf(contact.id) !== -1
    );

    // A contact that was just created is ticked for the user.
    if (this.justAddedId) {
      const added = this.allContacts.filter((c) => c.id === this.justAddedId)[0];
      if (added && this.isReachable(added) && !this.isSelected(added)) {
        this.chosen = this.chosen.concat([added]);
      }
      this.justAddedId = '';
    }
  }

  /** Id of the contact created by the last AddContact call, ticked on reload. */
  private justAddedId = '';

  // ======================================================================
  // Channel
  // ======================================================================

  setChannel(channel: SendChannel): void {
    if (this.channel === channel) {
      return;
    }
    this.channel = channel;
    // Switching channel can strip the only address a ticked contact had, so
    // drop anyone who just became unreachable rather than send nowhere.
    this.chosen = this.chosen.filter((contact) => this.isReachable(contact));
  }

  /** False when the contact has no address for the active channel. */
  isReachable(contact: WelcomeEmailContact): boolean {
    return this.channel === 'email' ? !!contact.email : !!contact.mobile;
  }

  /** Contacts in view that cannot be reached on the active channel. */
  get unreachableContacts(): WelcomeEmailContact[] {
    return this.contacts.filter((contact) => !this.isReachable(contact));
  }

  /** Total contacts loaded for this record, ignoring the search filter. */
  get totalContacts(): number {
    return this.allContacts.length;
  }

  /** First names of the unreachable contacts, for the warning strip. */
  get unreachableNames(): string {
    return this.unreachableContacts
      .map((contact) => (contact.name || '').split(/\s+/)[0])
      .filter((name) => !!name)
      .join(', ');
  }

  /** The channel the warning strip tells the user to switch to. */
  get otherChannelLabel(): string {
    return this.channel === 'email' ? 'Text' : 'Email';
  }

  /** "email" / "mobile", used in the warning copy. */
  get missingFieldLabel(): string {
    return this.channel === 'email' ? 'email' : 'mobile';
  }

  // ======================================================================
  // Contact selection
  // ======================================================================

  isSelected(contact: WelcomeEmailContact): boolean {
    return this.chosen.some((item) => item.id === contact.id);
  }

  toggleContact(contact: WelcomeEmailContact): void {
    if (!this.isReachable(contact)) {
      return; // nothing to send to -- the row is inert
    }
    this.chosen = this.isSelected(contact)
      ? this.chosen.filter((item) => item.id !== contact.id)
      : this.chosen.concat([contact]);
  }

  /** Space / Enter toggle a focused row, matching native checkbox behaviour. */
  onRowKeydown(event: KeyboardEvent, contact: WelcomeEmailContact): void {
    if (event.key === ' ' || event.key === 'Enter' || event.key === 'Spacebar') {
      event.preventDefault();
      this.toggleContact(contact);
    }
  }

  /** Ticks everyone reachable in the current result set. */
  selectAll(): void {
    const additions = this.contacts.filter(
      (contact) => this.isReachable(contact) && !this.isSelected(contact)
    );
    this.chosen = this.chosen.concat(additions);
  }

  clearSelection(): void {
    this.chosen = [];
  }

  get selectedCount(): number {
    return this.chosen.length;
  }

  // ======================================================================
  // Adding a contact (Contact/AddContact)
  // ======================================================================

  toggleAddRecipient(): void {
    this.showAddRecipient = !this.showAddRecipient;
    this.newContact = { name: '', email: '', phone: '' };
    this.manualError = '';

    // Field definitions are needed to build the AddContact payload; fetch
    // them once, the first time the form is opened.
    if (this.showAddRecipient && !this.contactFields.length) {
      this.loadContactFields();
    }
  }

  /** The Contact folder id, taken from the cached module list. */
  private get contactFolderId(): number {
    try {
      const modules = JSON.parse(sessionStorage.getItem('menuModules') || '[]');
      const match = modules.filter((m: any) => m.mainModuleName === 'Contact')[0];
      return match ? Number(match.folderID) : 0;
    } catch (e) {
      return 0;
    }
  }

  // Summary
  // Load the Contact folder's field definitions, so the new contact's name,
  // email and phone are sent against the server's real field ids.
  private loadContactFields(): void {
    const reqModel = {
      userID: this.data ? this.data.userID : null,
      organizationID: Number(this.data && this.data.organizationID) || 0,
      databaseID: Number(this.data && this.data.databaseID) || 0,
      folderID: this.contactFolderId,
      subFolderID: 0,
      fieldID: 0,
      requestFrom: 0,
      officeID: (this.data && this.data.officeID) || 0,
      OfficeIDList: (this.data && this.data.officeIDList) || [],
    };

    this.coreService.getCoreFeildList(reqModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: any) => {
        if (response && response.status === this.statusConst.statusSuccess) {
          const active = (response.data && response.data.activeFields) || [];
          this.contactFields = active.filter((field: any) => field.fieldID > 0);
        }
      });
  }

  /** One `Fields` entry, resolved against the server's field definitions. */
  private fieldEntry(fieldName: string, value: string): any {
    const match = this.contactFields.filter(
      (field: any) => (field.fieldName || '').toLowerCase() === fieldName.toLowerCase()
    )[0];

    return {
      FieldID: match ? match.fieldID : 0,
      FieldName: match ? match.fieldName : fieldName,
      FieldValue: value,
    };
  }

  // Summary
  // Create the contact and attach it to this record in one call: AddContact
  // takes `coreID`, which is how the Add New panel associates a new contact
  // with the core it was opened from. On success the picker reloads from
  // GetCoreSelectedContact so the new contact appears with its real id.
  addNewContact(): void {
    const name = (this.newContact.name || '').trim();
    const email = (this.newContact.email || '').trim();
    const phone = (this.newContact.phone || '').trim();

    if (!name) {
      this.manualError = 'Enter a name.';
      return;
    }
    if (!email) {
      this.manualError = 'Enter an email address.';
      return;
    }
    if (!this.emailPattern.test(email)) {
      this.manualError = 'Enter a valid email address.';
      return;
    }
    if (this.allContacts.some((c) => (c.email || '').toLowerCase() === email.toLowerCase())) {
      this.manualError = 'A contact with that email is already on this record.';
      return;
    }

    const fields = [this.fieldEntry('Name', name), this.fieldEntry('Email', email)];
    if (phone) {
      fields.push(this.fieldEntry('Phone', phone));
    }

    const payload = {
      UserID: this.data ? this.data.userID : null,
      DatabaseID: Number(this.data && this.data.databaseID) || 0,
      OrganizationID: Number(this.data && this.data.organizationID) || 0,
      FolderID: this.contactFolderId,
      officeID: (this.data && this.data.officeID) || 0,
      OfficeIDList: (this.data && this.data.officeIDList) || [],
      Fields: fields,
      Tags: '',
      TypeID: 0,
      ImageUrl: '',
      isShared: false,
      Status: '',
      // this is what attaches the new contact to the open record
      coreID: Number(this.data && this.data.coreID) || 0,
    };

    this.savingContact = true;
    this.manualError = '';

    this.coreService.saveContactFeildList(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.savingContact = false;

          if (!response || response.status !== this.statusConst.statusSuccess) {
            this.manualError =
              (response && response.message) || 'Could not add the contact.';
            return;
          }

          this.toastr.success('Contact added.', '', { timeOut: 3000 });
          this.justAddedId = response.id ? String(response.id) : '';
          this.showAddRecipient = false;
          this.newContact = { name: '', email: '', phone: '' };

          // Reload so the contact arrives with its server id and role.
          this.loadContacts();
        },
        () => {
          this.savingContact = false;
          this.manualError = 'Could not add the contact.';
        }
      );
  }

  // ======================================================================
  // Attach / schedule
  // ======================================================================

  onAttachmentsPicked(event: any): void {
    const files: FileList = event && event.target ? event.target.files : null;
    if (!files || !files.length) {
      return;
    }
    for (let i = 0; i < files.length; i++) {
      this.attachments = this.attachments.concat([files.item(i)]);
    }
    // Let the same file be picked again after it is removed.
    event.target.value = '';
  }

  removeAttachment(file: File): void {
    this.attachments = this.attachments.filter((item) => item !== file);
  }

  toggleSchedule(): void {
    this.showSchedule = !this.showSchedule;
    if (!this.showSchedule) {
      this.scheduledAt = '';
    }
  }

  // ======================================================================
  // Display helpers
  // ======================================================================

  /** Up to two initials for the avatar, ignoring titles like "Dr.". */
  initials(name: string): string {
    const parts = (name || '')
      .split(/\s+/)
      .filter((part) => !!part && !/^(dr|mr|mrs|ms|miss|prof)\.?$/i.test(part));
    if (!parts.length) {
      return '?';
    }
    const first = parts[0].charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  }

  /** Stable identity for *ngFor, so rows are not rebuilt on every search. */
  trackByContact(index: number, contact: WelcomeEmailContact): string {
    return contact.id;
  }

  // ======================================================================
  // Actions
  // ======================================================================

  /** Guards the Prepare Message button while a draft is in flight. */
  preparingMessage = false;

  get canPrepareMessage(): boolean {
    return !!(this.personalNoteTitle || '').trim() && !this.preparingMessage;
  }

  // Summary
  // Turn the message title into a drafted heading and body. The title the user
  // typed is the prompt; the tone and length are fixed for this dialog. On
  // success both note fields are overwritten with what came back.
  prepareMessage(): void {
    this.runPrepare((this.personalNoteTitle || '').trim());
  }

  // Summary
  // The one call behind both the Prepare Message button and a prompt picked in
  // the templates popup -- the only difference between them is where the
  // prompt text comes from.
  private runPrepare(prompt: string): void {
    if (!prompt ) //|| this.preparingMessage) {
     {
       return;
    }

    this.postPrepare(this.buildPrepareRequest(prompt, 200));
  }

  /** The request body, with only the prompt and the length varying by caller. */
  private buildPrepareRequest(prompt: string, length: number): PreparePersonalMessageRequest {
    return {
      templateID: 0,
      prompt,
      // The chip row as it stands, additions and removals included.
      conditions: this.conditions.map((condition) => condition.name).join(','),
      tome: 'Professional',
      length,
      userID: this.data && this.data.userID ? String(this.data.userID) : '',
      organizationID: Number(this.data && this.data.organizationID) || 0,
      officeID: Number(this.data && this.data.officeID) || 0,
      databaseID: Number(this.data && this.data.databaseID) || 0,
    };
  }

  private postPrepare(request: PreparePersonalMessageRequest): void {
    this.preparingMessage = true;

    this.coreService.preparePersonalMessage(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.preparingMessage = false;

          if (!response || response.status !== this.statusConst.statusSuccess) {
            this.toastr.error(
              (response && response.message) || 'Could not prepare the message.',
              '', { timeOut: 4000 });
            return;
          }

          const draft: PreparePersonalMessageResponse = response.data || {};

          if (draft.title) {
            this.personalNoteTitle = draft.title;
          }
          this.personalNote = draft.message || '';
        },
        () => {
          this.preparingMessage = false;
          this.toastr.error('Could not prepare the message.', '', { timeOut: 4000 });
        }
      );
  }

  // ======================================================================
  // Personal message prompt templates
  // (EmailTemplate/GetPersonalMessagePromptTemplates)
  // ======================================================================

  promptTemplatesDialogRef: MatDialogRef<any>;
  promptTemplates: PersonalMessagePromptTemplate[] = [];
  promptTemplatesLoading = false;
  promptTemplatesError = '';

  /** The inline "add a prompt" form inside the popup. */
  showAddPromptTemplate = false;
  newPromptTemplate = '';
  savingPromptTemplate = false;

  // -1 asks for every tier at once, so the popup lists Office, Organization
  // and Global prompts together.
  private readonly promptTemplateLevel = -1;

  /** Row labels for the tier a prompt was stored at. */
  private readonly promptLevelLabels = ['Office', 'Organization', 'Global'];

  promptLevelLabel(level: number): string {
    return this.promptLevelLabels[level] || '';
  }

  openPromptTemplates(templateRef: TemplateRef<any>): void {
    this.showAddPromptTemplate = false;
    this.newPromptTemplate = '';

    this.promptTemplatesDialogRef = this.dialog.open(templateRef, {
      panelClass: 'custom-modalbox',
      // the same width the Send Template dialog is showing at
      width: this.showEducationalPanel ? this.wideDialogWidth : this.baseDialogWidth,
    });

    this.loadPromptTemplates();
  }

  closePromptTemplates(): void {
    if (this.promptTemplatesDialogRef) {
      this.promptTemplatesDialogRef.close();
    }
  }

  private promptTemplateRequestBase() {
    return {
      userID: this.data && this.data.userID ? String(this.data.userID) : '',
      organizationID: Number(this.data && this.data.organizationID) || 0,
      officeID: Number(this.data && this.data.officeID) || 0,
      databaseID: Number(this.data && this.data.databaseID) || 0,
    };
  }

  loadPromptTemplates(): void {
    const request = Object.assign(
      { level: this.promptTemplateLevel }, this.promptTemplateRequestBase());

    this.promptTemplatesLoading = true;
    this.promptTemplatesError = '';

    this.settingService.getPersonalMessagePromptTemplates(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.promptTemplatesLoading = false;

          if (!response || response.status !== this.statusConst.statusSuccess) {
            this.promptTemplates = [];
            this.promptTemplatesError =
              (response && response.message) || 'Could not load the prompts.';
            return;
          }

          const rows: any[] = Array.isArray(response.data) ? response.data : [];

          this.promptTemplates = rows.map((row) => ({
            templateID: Number(row.templateID) || 0,
            prompt: row.prompt || '',
            level: Number(row.level) || 0,
          }));
        },
        () => {
          this.promptTemplatesLoading = false;
          this.promptTemplates = [];
          this.promptTemplatesError = 'Could not load the prompts.';
        }
      );
  }

  retryPromptTemplates(): void {
    this.loadPromptTemplates();
  }

  trackByPromptTemplate(index: number, row: PersonalMessagePromptTemplate): number {
    return row.templateID;
  }

  // Summary
  // Use a stored prompt straight away: the popup closes and the note fields are
  // redrafted from it, leaving the title the user typed out of it entirely.
  usePromptTemplate(row: PersonalMessagePromptTemplate): void {
    const prompt = (row.prompt || '').trim();

    if (!prompt) {
      return;
    }

    this.closePromptTemplates();
    this.runPrepare(prompt);
  }

  toggleAddPromptTemplate(): void {
    this.showAddPromptTemplate = !this.showAddPromptTemplate;
    this.newPromptTemplate = '';
  }

  get canSavePromptTemplate(): boolean {
    return !!this.newPromptTemplate.trim() && !this.savingPromptTemplate;
  }

  // Summary
  // Store a new prompt at the same level the list was read from, then reload so
  // it appears in the list and can be used right away.
  addPromptTemplate(): void {
    const prompt = this.newPromptTemplate.trim();

    if (!prompt || this.savingPromptTemplate) {
      return;
    }

    const request = Object.assign(
      { templateID: 0, prompt }, this.promptTemplateRequestBase());

    this.savingPromptTemplate = true;

    this.settingService.addEditPersonalMessagePromptTemplate(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.savingPromptTemplate = false;

          if (!response || response.status !== this.statusConst.statusSuccess) {
            this.toastr.error(
              (response && response.message) || 'Could not save the prompt.',
              '', { timeOut: 4000 });
            return;
          }

          this.showAddPromptTemplate = false;
          this.newPromptTemplate = '';
          this.loadPromptTemplates();
        },
        () => {
          this.savingPromptTemplate = false;
          this.toastr.error('Could not save the prompt.', '', { timeOut: 4000 });
        }
      );
  }

  get canSend(): boolean {
    return this.selectedCount > 0 && !this.sending;
  }

  /** The `people` array exactly as the endpoint expects it. */
  private buildPeople(): EducationalMaterialPerson[] {
    return this.chosen.map((contact) => ({
      id: Number(contact.id) || 0,
      email: contact.email || '',
      // the endpoint wants a first name, and the list carries the full name
      firstName: (contact.name || '').trim().split(/\s+/)[0] || '',
      phone: contact.mobile || '',
    }));
  }

  // Summary
  // Send the educational material to the ticked contacts, then close with the
  // payload so the caller can refresh. The dialog stays open on failure with
  // the selection intact, so the user can retry without rebuilding it.
  send(): void {
    if (!this.canSend) {
      return;
    }

    const people = this.buildPeople();

    const personalMessageTitle = (this.personalNoteTitle || '').trim();
    const personalMessage = (this.personalNote || '').trim();

    const request: SendEducationalMaterialRequest = {
      people,
      // The chip row as it stands, additions and removals included.
      conditions: this.conditions.map((condition) => condition.name).join(','),
      files: this.buildFiles(),
      coreID: Number(this.data && this.data.coreID) || 0,
      coreName: this.data && this.data.subjectName ? this.data.subjectName : '',
      personalMessageTitle,
      personalMessage,
      // the endpoint takes the channel as a number: 0 = Email, 1 = Text
      sendVia: this.channel === 'text' ? 1 : 0,
      userID: this.data && this.data.userID ? String(this.data.userID) : '',
      organizationID: Number(this.data && this.data.organizationID) || 0,
      officeID: Number(this.data && this.data.officeID) || 0,
      databaseID: Number(this.data && this.data.databaseID) || 0,
    };

    this.sending = true;

    this.coreService.sendCoreEducationalMaterialToContacts(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.sending = false;

          if (!response || response.status !== this.statusConst.statusSuccess) {
            this.toastr.error(
              (response && response.message) || 'Could not send the material.',
              '', { timeOut: 4000 });
            return;
          }

          const result: WelcomeEmailResult = {
            people,
            contacts: this.chosen.slice(),
            channel: this.channel,
            personalMessageTitle,
            personalMessage,
          };
          this.dialogRef.close(result);
        },
        () => {
          this.sending = false;
          this.toastr.error('Could not send the material.', '', { timeOut: 4000 });
        }
      );
  }

  cancel(): void {
    this.dialogRef.close();
  }

  peoples: any = [] = [];
  contactsList: any = [] = [];
  isSharedAccessRolePopup: boolean = false;
  isRolePopup: boolean = false;
  showAddButton: boolean = false;
  shareAddDialogRef: MatDialogRef<any>;
  isShare: boolean = false;
  searchName: string = "";
  coreID: number = 0;
  responseHolder: any = [] = [];
  addnewContact: boolean = false;
  showAddContact: boolean = false;
  Module: string = '';

  close() {
    this.peoples = [];
    this.shareAddDialogRef.close();
  }

  contactSelected(contactSelected: any) {
    if (contactSelected.contactID == 0) {
      // this.dialog.closeAll();
      //this.addNew('Contact');
      this.addNew(this.ContactName);
      this.close();
      return;
    }
    let contact = this.peoples.find((m: any) => m.contactID == contactSelected.contactID);

    if (contact == undefined || contact == '' || contact == null) {
      this.peoples.push({
        name: contactSelected.name,
        email: contactSelected.email,
        img: contactSelected.imageUrl,
        contactID: contactSelected.contactID,
        roleID: 0,
        isShared: this.isShare,
        isOwner: false,
        shortName: contactSelected.shortName,
        CoreID: Number(sessionStorage.getItem("transactionID"))
      });
      if (this.peoples.length > 0) {
        this.showAddButton = true;
      }
      else {
        this.showAddButton = false;
      }
    }
  }

  addNew(module: any) {
    this.Module = module;
    this.showAddContact = !this.showAddContact;
  }

  getClose(message: any) {
    this.showAddContact = message;
  }

  getContactListCoreImplementation(searchValue: any) {
    this.filteredContactVm.DatabaseID = Number(this.data && this.data.databaseID) || 0;
    this.filteredContactVm.OrganizationID = Number(this.data && this.data.organizationID) || 0;
    this.filteredContactVm.UserID = this.data && this.data.userID ? String(this.data.userID) : '';
    this.filteredContactVm.searchValue = searchValue;
    this.searchName = searchValue;
    this.filteredContactVm.CoreID = Number(sessionStorage.getItem("transactionID"));
    this.coreID = Number(sessionStorage.getItem("transactionID"));
    this.filteredContactVm.PageSize = 0;
    this.filteredContactVm.officeID = Number(this.data && this.data.officeID) || 0;
    this.coreService.GetContactListCoreImplementation(this.filteredContactVm).subscribe(response => {
      if (response != undefined) {
        this.responseHolder = response;
        if (this.responseHolder.status == this.statusConst.statusSuccess) {
          this.contactsList = response.data.contactListModel;
          this.contactsList.forEach((e: any) => {
            e.shortName = e.name.match(/\b(\w)/g).join('');
          });
          this.contactsList.push(
            {
              "contactID": 0,
              "coreID": 0,
              "name": "Create New",
              "email": "",
              "imageUrl": "",
              "isShared": this.isShare,
              "coreContactUserMapID": 0,
              "trasactionalRoleIds": [],
              "contactCoreRoleMappingModels": [],
              "shortName": "CN"
            }
          )
        }
      } else {
        this.loaderService.hide();
      }
    });
  }

  removeItem(item: any) {
    const index = this.peoples.indexOf(item);
    if (index >= 0) {
      this.peoples.splice(index, 1);
    }
    if (this.peoples.length == 0) {
      this.showAddButton = false;
    }
  }

  addUpdateCoreContact() {
    if (this.peoples.length > 0) {
      //this.dialog.closeAll();
      var reqModel = {
        databaseID: Number(this.data && this.data.databaseID) || 0,
        organizationID: Number(this.data && this.data.organizationID) || 0,
        userID: this.data && this.data.userID ? String(this.data.userID) : '',
        addUpdateCoreContactListModel: this.peoples,
        officeID: Number(this.data && this.data.officeID) || 0
      }
      this.loaderService.show();
      this.coreService.addUpdateCoreContact(reqModel).subscribe(response => {
        if (response != undefined) {
          this.loaderService.hide();
          this.responseHolder = response;
          if (this.responseHolder.status == this.statusConst.statusSuccess) {
            this.peoples = [];
            this.showAddButton = false;
            this.loadContacts();
            this.syncSelection();
            this.close();
          }
        } else {
          this.loaderService.hide();
        }
      });
    }
  }

  openPopupAddContact(templateRef: TemplateRef<any>) {
    this.contactsList = [];
    this.peoples = [];
    this.showAddButton = false;
    this.shareAddDialogRef = this.dialog.open(templateRef, {
      panelClass: 'custom-modalbox'
    });
  }

  getNormalAndSharedContacts() {
    this.loadContacts();
    this.syncSelection();
  }


  onIncludeEmailChange(event: any): void {
    const coreId = Number(this.coreID);

    if (event.checked) {

      // Add Core contact at first position if not already present
      const isCoreAlreadyAdded = this.allContacts.some((x: any) => Number(x.id) === coreId);

      if (!isCoreAlreadyAdded && this.coreDetails?.length > 0) {

        const emails = this.coreDetails.find((x: any) => x.fieldName === 'Email');

        const mobile = this.coreDetails.find((x: any) => x.fieldName === 'Phone');

        const name = this.coreDetails.find((x: any) => x.fieldName === 'Name');

        const coreContact = {
          badge: "",
          email: emails?.fieldValue || "",
          id: String(this.coreID),
          mobile: mobile?.fieldValue || "",
          name: name?.fieldValue || "",
          prefers: "email",
          relationship: this.coreName
        };

        // Add Core contact at first position
        this.allContacts.unshift(coreContact);
      }

    } else {
      // Remove Core contact
      this.allContacts = this.allContacts.filter((x: any) => Number(x.id) !== coreId);
    }
    this.applyFilter();
  }



}

